import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RefreshCw, User, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState } from '../components/ui';

type Row = {
  id: string;
  display_name: string;
  type: 'freelance' | 'staff';
  verification_status: string;
  is_active: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
  photo_url: string | null;
  home_club_id: string | null;
  clubs: { name: string } | null;
  created_at: string;
};

const STATUS_FILTERS: Array<{ key: string | null; label: string }> = [
  { key: null, label: 'All' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'ai_reviewed', label: 'AI reviewed' },
  { key: 'verified', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
];

export default function Coaches() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from('coaches')
      .select(`
        id, display_name, type, verification_status, is_active,
        stripe_account_id, stripe_onboarding_completed_at, photo_url,
        home_club_id, created_at,
        clubs:home_club_id(name)
      `)
      .order('created_at', { ascending: false });
    if (status) q = q.eq('verification_status', status);
    const { data } = await q;
    setRows((data as unknown as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [status]);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Coaches"
        subtitle="Review credentials and Stripe onboarding for every coach on the marketplace."
        actions={<Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>}
      />

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {STATUS_FILTERS.map((f) => {
          const active = status === f.key;
          return (
            <button
              key={f.label}
              onClick={() => setStatus(f.key)}
              className={`h-8 px-3 rounded-full text-xs whitespace-nowrap transition-colors ${
                active ? 'bg-[#2d5016] text-white' : 'bg-white border border-gray-300 text-gray-700'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading coaches…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={User}
            title="No coaches in this filter"
            description="Change the filter above, or wait for new applications to come in."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Coach</th>
                <th className="text-left px-5 py-3 font-semibold">Type</th>
                <th className="text-left px-5 py-3 font-semibold">Home club</th>
                <th className="text-left px-5 py-3 font-semibold">Verification</th>
                <th className="text-left px-5 py-3 font-semibold">Stripe</th>
                <th className="text-left px-5 py-3 font-semibold">Active</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => nav(`/coaches/${c.id}`)}
                  className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                        {c.photo_url
                          ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                          : <User className="w-4 h-4 text-gray-400" />}
                      </div>
                      <span className="font-medium text-gray-900">{c.display_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 capitalize text-gray-700">{c.type}</td>
                  <td className="px-5 py-3 text-gray-600">{c.clubs?.name ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Badge tone={
                      c.verification_status === 'verified' ? 'green'
                      : c.verification_status === 'rejected' ? 'red'
                      : c.verification_status === 'ai_reviewed' ? 'blue'
                      : 'amber'
                    } icon={c.verification_status === 'verified' ? CheckCircle2 : c.verification_status === 'rejected' ? AlertTriangle : undefined}>
                      {c.verification_status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    {c.stripe_onboarding_completed_at
                      ? <Badge tone="green">Connected</Badge>
                      : c.stripe_account_id
                        ? <Badge tone="amber">KYC pending</Badge>
                        : <Badge tone="gray">Not started</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {c.is_active ? <Badge tone="green">Yes</Badge> : <Badge tone="gray">No</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
